import { IconCloudDownload, IconFileUpload, IconInfoCircle } from "@tabler/icons-react";
import { useRef } from "react";
import { Alert, Badge, Button, Card, Group, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { formatFecha, formatFechaHora } from "@/shared/format/fecha";
import type { ActualizacionClavesSat, EstadoActualizacion, OrigenActualizacion } from "../services/claves-sat-admin-service";
import {
  useActualizacionesClavesSat,
  useActualizarDesdeSat,
  useSubirCatalogoClavesSat,
} from "../hooks/use-actualizaciones-claves-sat";

const ETIQUETA_ORIGEN: Record<OrigenActualizacion, string> = {
  Sat: "Sitio del SAT (botón)",
  Archivo: "Archivo subido",
  Programada: "Revisión programada",
};

const ESTADO: Record<EstadoActualizacion, { etiqueta: string; color: string }> = {
  EnCurso: { etiqueta: "En curso", color: "info" },
  Completada: { etiqueta: "Completada", color: "exito" },
  SinCambios: { etiqueta: "Sin cambios", color: "slate" },
  Fallida: { etiqueta: "Fallida", color: "peligro" },
};

/**
 * Actualización del catálogo de claves producto/servicio con el XLS oficial del SAT. El administrador puede
 * pedir la descarga del catálogo vigente (el sistema localiza el enlace «Catálogos CFDI versión 4.0» en la
 * página del Anexo 20), subir el XLS a mano si el sitio del SAT no responde, y revisar el historial. Además hay
 * una revisión programada semanal (se configura en /admin/configuraciones · «CatalogoSat»).
 */
export function ActualizacionCatalogoPanel() {
  const { data } = useActualizacionesClavesSat();
  const actualizar = useActualizarDesdeSat();
  const subir = useSubirCatalogoClavesSat();
  const entradaArchivo = useRef<HTMLInputElement>(null);

  const enCurso = data?.enCurso ?? false;
  const ocupado = enCurso || actualizar.isPending || subir.isPending;
  const historial = data?.items ?? [];
  const ultimaExitosa = historial.find((a) => a.estado === "Completada" || a.estado === "SinCambios");

  const alElegirArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (archivo) subir.mutate(archivo);
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div className="min-w-0">
            <Text fw={600}>Actualizar desde el catálogo del SAT</Text>
            <Text size="sm" c="dimmed" className="mt-0.5">
              {ultimaExitosa
                ? <>Última actualización: {formatFechaHora(ultimaExitosa.terminadaAt)} · archivo {ultimaExitosa.archivoNombre ?? "—"}.</>
                : "Aún no se ha actualizado con el catálogo del SAT."}
            </Text>
          </div>
          <Group gap="xs">
            <Button leftSection={<IconCloudDownload size={14} />} loading={enCurso || actualizar.isPending} disabled={ocupado} onClick={() => actualizar.mutate()}>
              {enCurso ? "Actualizando…" : "Actualizar desde el SAT"}
            </Button>
            <Button variant="default" leftSection={<IconFileUpload size={14} />} disabled={ocupado} onClick={() => entradaArchivo.current?.click()}>
              Subir archivo XLS
            </Button>
            {/* Oculto visualmente (no display:none) para que el clic programático abra el selector. */}
            <input
              ref={entradaArchivo}
              type="file"
              accept=".xls,.xlsx"
              onChange={alElegirArchivo}
              style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
          </Group>
        </Group>

        <Alert variant="info" icon={<IconInfoCircle size={16} stroke={1.7} />}>
          El catálogo del SAT manda: sus claves nuevas se crean, las existentes toman su descripción, vigencia y demás
          columnas, y las que el SAT dejó de publicar se marcan no vigentes (no se borran: pueden estar en prorrateos
          históricos). Una clave no vigente o fuera de su fecha de vigencia no cuenta como catalogada al confirmar el
          prorrateo. El proceso corre en segundo plano y puede tardar un par de minutos.
        </Alert>

        {historial.length > 0 && (
          <div className="overflow-x-auto">
            <Table verticalSpacing="xs">
              <Thead>
                <Tr>
                  <Th>Inicio</Th>
                  <Th>Origen</Th>
                  <Th>Archivo</Th>
                  <Th>Estado</Th>
                  <Th align="right">En archivo</Th>
                  <Th align="right">Nuevas</Th>
                  <Th align="right">Actualizadas</Th>
                  <Th align="right">Descontinuadas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {historial.map((a) => (
                  <FilaActualizacion key={a.id} a={a} />
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Stack>
    </Card>
  );
}

function FilaActualizacion({ a }: { a: ActualizacionClavesSat }) {
  const estado = ESTADO[a.estado];
  const terminada = a.estado !== "EnCurso";
  return (
    <Tr>
      <Td className="whitespace-nowrap">
        <Text size="sm">{formatFechaHora(a.iniciadaAt)}</Text>
        {a.solicitadaPorEmail && (
          <Text size="xs" c="dimmed">
            {a.solicitadaPorEmail}
          </Text>
        )}
      </Td>
      <Td>
        <Text size="sm">{ETIQUETA_ORIGEN[a.origen]}</Text>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {a.archivoNombre ?? "—"}
        </Text>
        {a.fechaPublicacionCatalogo && (
          <Text size="xs" c="dimmed">
            Publicado {formatFecha(a.fechaPublicacionCatalogo)}
          </Text>
        )}
      </Td>
      <Td>
        <Badge color={estado.color} variant="light">
          {estado.etiqueta}
        </Badge>
        {a.estado === "Fallida" && a.error && (
          <Text size="xs" c="peligro" className="mt-1 max-w-[320px]" title={a.error}>
            {a.error.length > 140 ? `${a.error.slice(0, 140)}…` : a.error}
          </Text>
        )}
      </Td>
      <Td align="right" className="tabular-nums">{terminada && a.estado !== "Fallida" ? a.totalEnArchivo.toLocaleString("es-MX") : "—"}</Td>
      <Td align="right" className="tabular-nums">{terminada && a.estado !== "Fallida" ? a.creadas.toLocaleString("es-MX") : "—"}</Td>
      <Td align="right" className="tabular-nums">{terminada && a.estado !== "Fallida" ? a.actualizadas.toLocaleString("es-MX") : "—"}</Td>
      <Td align="right" className="tabular-nums">{terminada && a.estado !== "Fallida" ? a.descontinuadas.toLocaleString("es-MX") : "—"}</Td>
    </Tr>
  );
}
